import { forwardRef, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  FaCalendar,
  FaCheckCircle,
  FaClock,
  FaSync,
  FaFileArchive,
  FaVideo,
} from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";

import { useAuth } from "src/hooks/useAuth";
import { useCard } from "src/hooks/useCard";
import { useCardSegments } from "src/hooks/useCardSegments";
import { useCardCompilations } from "src/hooks/useCardCompilations";
import {
  getIsDueDatePassed,
  isCardInArchiveAlertWindow,
} from "src/libs/cardUtils";
import { LoadingStatus } from "src/libs/fetcher";
import { GroupVideoListThumbnail } from "src/components/GroupVideosList/GroupVideoListThumbnail";
import {
  getIsAnyCompilationRendering,
  getIsRecentCompilationComplete,
} from "@memento/library/compilation";
import { getPluralEnding } from "@memento/library/text";
import config from "src/config";
import { useCompilationThemes } from "src/components/Renderer/Pages/Theme/useCompilationThemes";

type GroupVideoListItemProps = {
  cardId: string;
  cardUrl: string;
  isContributor?: boolean;
};

export const GroupVideoListItem = forwardRef<
  HTMLDivElement,
  GroupVideoListItemProps
>(function GroupVideoListItem({ cardId, cardUrl, isContributor }, ref) {
  const { user } = useAuth();
  const useCardQuery = useCard(cardId, cardUrl);
  const useCardCompilationsQuery = useCardCompilations(cardId, cardUrl);
  const useCardSegmentsQuery = useCardSegments(cardId, cardUrl);
  const { themes } = useCompilationThemes();
  const compilations = useCardCompilationsQuery.result?.compilations;
  const card = useCardQuery.result?.card;

  const {
    isArchived,
    isToBeArchived,
    isCompilationComplete,
    dueDatePassed,
    numActiveSegments,
    isAnyCompilationRendering,
  } = useMemo(() => {
    const activeSegments = useCardSegmentsQuery.result?.activeSegments || [];
    const numActiveSegments = activeSegments.length;

    const dueDatePassed = card?.dueDate && getIsDueDatePassed(card.dueDate);

    const isAnyCompilationRendering = getIsAnyCompilationRendering(
      compilations || [],
      activeSegments,
      themes || [],
      config.COMP_STUCK_MINUTES
    );

    const isCompilationComplete = getIsRecentCompilationComplete(
      compilations || [],
      activeSegments,
      themes || [],
      config.COMP_STUCK_MINUTES
    );

    const isArchived = Boolean(useCardQuery.result?.isArchived);
    const isToBeArchived = isCardInArchiveAlertWindow(card, compilations);

    return {
      isArchived,
      isToBeArchived,
      isCompilationComplete,
      dueDatePassed,
      numActiveSegments,
      isAnyCompilationRendering,
    };
  }, [
    themes,
    card,
    compilations,
    useCardQuery.result?.isArchived,
    useCardSegmentsQuery.result?.activeSegments,
  ]);

  return (
    <div
      ref={ref}
      className="grid grid-cols-4 grid-rows-5 items-center overflow-hidden rounded-2xl border border-gray-200 shadow-xl sm:grid-cols-6 sm:grid-rows-1 lg:grid-cols-4"
    >
      <div className="relative col-span-4 row-span-3 overflow-hidden sm:col-span-2 lg:col-span-1">
        <GroupVideoListThumbnail
          cardId={card?.cardId}
          cardUrl={card?.cardUrl}
          user={user}
          isContributor={isContributor}
        />
      </div>
      <section className="col-span-3 row-span-2 space-y-2 pr-2 pl-8 sm:col-span-3 lg:col-span-2">
        <h2 className="text-xl font-medium sm:font-normal">{card?.title}</h2>
        <div className="space-y-1">
          {isArchived ? (
            <div className="flex items-center space-x-2">
              <FaFileArchive className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">Archived</span>
            </div>
          ) : null}
          {isToBeArchived ? (
            <div className="flex items-center space-x-2">
              <FaClock className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">Soon to be archived</span>
            </div>
          ) : null}
          {isCompilationComplete && !isArchived ? (
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">Group Video done</span>
            </div>
          ) : null}
          {isAnyCompilationRendering && !isArchived ? (
            <div className="flex items-center space-x-2">
              <FaSync className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">Making Group Video</span>
            </div>
          ) : null}
          {!dueDatePassed && !isCompilationComplete ? (
            <div className="flex items-center space-x-2">
              <FaCalendar className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">
                Clips due{" "}
                {card?.dueDate
                  ? formatDistanceToNow(card.dueDate, { addSuffix: true })
                  : null}
              </span>
            </div>
          ) : null}
          {isCompilationComplete && dueDatePassed ? (
            <div className="flex items-center space-x-2">
              <FaCalendar className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">
                {card?.dueDate
                  ? formatDistanceToNow(card.dueDate, { addSuffix: true })
                  : null}
              </span>
            </div>
          ) : null}
          {useCardSegmentsQuery.status !== LoadingStatus.LOADING &&
          !isArchived ? (
            <div className="flex items-center space-x-2">
              <FaVideo className="h-4 w-4 text-red-700" />
              <span className="text-sm text-red-700">
                {numActiveSegments} clip{getPluralEnding(numActiveSegments)}
              </span>
            </div>
          ) : null}
        </div>
      </section>
      <div className="col-span-1 row-span-2 self-end justify-self-end py-4 pr-8 pl-4 sm:self-center sm:py-0">
        <div className="rounded-full bg-gray-100 p-2">
          <FiChevronRight className="h-6 w-6 text-gray-700" />
        </div>
      </div>
    </div>
  );
});
